import { useEffect } from 'react';

import {
	NPS_SURVEY_SUBMIT_FAILED,
	NPS_SURVEY_SUBMIT_SUCCESS,
	PN_INBOX_PAGE_LOAD,
	textConstant,
} from '@/constants';
import { useMerchantPermissions } from '@/hooks/useMerchantPermissions';
import { INotificationState, NotificationLink, RouteId } from '@/types';
import {
	sendClickEvent,
	sendCustomAnalyticsEvent,
} from '@/utils/AnalyticsUtils';
import BrickbatUtility from '@/utils/BrickbatUtils';
import { Toast } from '@/widgets';

import { useAuthGuard } from '../authGuard/useAuthGuard';

const { FEEDBACK_SHARED_MSG } = textConstant;

function getNotificationLinks(): Record<string, NotificationLink> {
	return {
		DASHBOARD: {
			link: '/dashboard',
			routeId: RouteId.DASHBOARD_PAGE,
		},
		LIVE_TRANSACTIONS: {
			link: '/transactions/live',
			routeId: RouteId.LIVE_TRANSACTIONS_PAGE,
		},
		TRANSACTIONS_HISTORY: {
			link: '/transactions/history',
			routeId: RouteId.TRANSACTIONS_HISTORY_PAGE,
		},
		TRANSACTION_REPORTS: {
			link: '/reports/transaction-reports',
			routeId: RouteId.TRANSACTION_REPORTS_PAGE,
		},
		TRANSACTION_DAILY_REPORTS: {
			link: '/reports/transaction-daily-reports',
			routeId: RouteId.DAILY_TRANSACTION_REPORTS_PAGE,
		},
		PAY_LINKS: {
			link: '/pay-links',
			routeId: RouteId.OFFLINE_PAYLINKS_PAGE,
		},
		SETTLEMENTS: {
			link: '/settlements',
			routeId: RouteId.SETTLEMENTS_PAGE,
		},
		MAPPED_QR: {
			link: '/qr/enabledQR',
			routeId: RouteId.ENABLED_QRS_PAGE,
		},
		STORE_LIST: {
			link: '/store/list',
			routeId: RouteId.STORES_PAGE,
		},
		PROFILE: {
			link: '/profile',
			routeId: RouteId.USER_PROFILE_PAGE,
		},
		TICKETS: {
			link: '/faq?tickets=true',
			routeId: RouteId.FAQS_PAGE,
		},
	};
}

export const useNotificationsDrawer = (notifications: INotificationState) => {
	const { merchantType } = useMerchantPermissions();
	const { shouldAccessRoute } = useAuthGuard();

	const readNotifications = notifications.read ?? [];

	const unreadNotifications = notifications.unread ?? [];

	const totalMessageCount =
		readNotifications.length + unreadNotifications.length;

	function getNotificationLink(linkToken: string) {
		const links = getNotificationLinks();
		if (!links[linkToken]) {
			try {
				const validURL = new URL(linkToken);
				return validURL;
			} catch (error) {
				return null;
			}
		}
		return shouldAccessRoute(links[linkToken]?.routeId)
			? links[linkToken]?.link
			: null;
	}

	const handleBrickbatNotification = (campaignId: string | undefined) => {
		if (!campaignId) return;
		BrickbatUtility.openSurvey(
			campaignId,
			() => {
				sendCustomAnalyticsEvent('custom', {
					name: NPS_SURVEY_SUBMIT_SUCCESS,
					value: campaignId,
				});
				Toast.success(FEEDBACK_SHARED_MSG, { duration: 2 });
			},
			() => {
				sendCustomAnalyticsEvent('custom', {
					name: NPS_SURVEY_SUBMIT_FAILED,
					value: campaignId,
				});
			},
		);
	};

	useEffect(() => {
		const unreadNotificationsCount = unreadNotifications.length ?? 0;
		sendClickEvent({
			name: PN_INBOX_PAGE_LOAD,
			additionalData: {
				page: document.location.pathname,
				notificationCount: unreadNotificationsCount,
				merchantType: merchantType,
			},
		});
	}, []);

	return {
		readNotifications,
		unreadNotifications,
		totalMessageCount,
		merchantType,
		getNotificationLink,
		handleBrickbatNotification,
	};
};
